/**
 * 持卡人认证 API（US-002 契约）
 *
 * FE-E2b 薄壳化：五件套模板（login/logout/refresh/me/menus）下沉
 * @shared/api/auth 工厂；本文件保留端配置（偏好联动钩子 / cardholderId 纠正链）、
 * 邀请注册对与类型，导出面不变（调用方零改动）。
 */
import {
  cardholderGet,
  cardholderPost,
  clearSession,
  DEFAULT_CARDHOLDER_ID,
  DEFAULT_MERCHANT_ID,
  loadSession,
  saveSession,
  signOutLocal,
  USE_MOCK,
  type ApiResult,
  type CardholderSession,
  type MenuNodeView,
  type PortalSessionView,
} from './client';
import { createAuthApi, type TokenPair } from '@shared/api/auth';
import { applyServerLocale, fetchPreferences } from './preferences';

export type { TokenPair, MenuNodeView };

const auth = createAuthApi<CardholderSession, PortalSessionView>({
  get: cardholderGet,
  post: cardholderPost,
  loadSession,
  saveSession,
  clearSession,
  signOutLocal,
  mockMe: () => {
    const s = loadSession();
    const id = Number(s?.cardholderId ?? DEFAULT_CARDHOLDER_ID);
    return {
      subjectId: id,
      channel: 'CARDHOLDER',
      principal: s?.principal || 'cardholder@mock',
      roles: [],
      permissions: [],
      cardholderId: id,
      // mock 默认已验证；仅当会话显式 emailVerified===false 时拦写
      emailVerified: s?.me?.emailVerified !== false,
      emailMasked: s?.me?.emailMasked,
      mustChangePassword: s?.me?.mustChangePassword === true,
      // S10a：CARDHOLDER 不再强制 2FA；门禁语义 = 是否已设支付密码
      mfaSetupRequired: false,
      otpGraceRemainingSeconds: s?.me?.otpGraceRemainingSeconds ?? 0,
      payPasswordSet: s?.me?.payPasswordSet === true,
      payPasswordSetupRequired: s?.me?.payPasswordSet !== true,
      sensitiveVerifyMode: 'PAY_PASSWORD_OR_2FA',
    };
  },
  // cardholderId：优先 register 已写会话 → 代码默认；随后用 /auth/me 纠正
  buildSession: (t: TokenPair, principal: string): CardholderSession => {
    const prev = loadSession();
    const fallbackId = prev?.cardholderId || '30';
    return {
      accessToken: t.accessToken,
      refreshToken: t.refreshToken,
      cardholderId: fallbackId,
      principal,
      expiresAt: t.expiresIn ? Date.now() + t.expiresIn * 1000 : undefined,
    };
  },
  // H5（US-PREF-2）：登录后拉取偏好取 locale 设定 i18n。
  // fire-and-forget 容错：fetchPreferences 失败/超时不阻塞登录（语言回落既有链），
  // 优先级链（URL > 服务端 > localStorage > 默认）在 applyServerLocale 内唯一实现。
  afterLogin: () => {
    void fetchPreferences().then((prefsRes) => {
      if (prefsRes.ok) {
        applyServerLocale(prefsRes.data.locale);
      }
    });
  },
  // me 成功时以 JWT 主体为准写入 cardholderId
  mergeAfterLogin: (current, meRes, menusRes) => {
    const me = meRes.ok ? meRes.data : null;
    const fromMe =
      me?.cardholderId != null
        ? String(me.cardholderId)
        : me?.subjectId != null
          ? String(me.subjectId)
          : current.cardholderId;
    return {
      ...current,
      cardholderId: fromMe,
      principal: me?.principal || current.principal,
      me,
      menus: menusRes.ok ? menusRes.data : null,
    };
  },
});

export const { fetchMe, refreshSessionMe, fetchMenus, refreshTokens, logout, login, logoutLocal } = auth;

// ================= 代登录（2026-10-23 · spec §13.9 持卡人轨） =================

/** AUTH-012 cardholder 变体响应：刻意无 refreshToken（契约 D3，代登录会话不可续期） */
export type PortalLoginResponse = {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  impersonated: boolean;
  principal: string;
  subjectId: number;
  cardholderId: number;
};

/**
 * 消费一次性票据换取持卡人会话：POST /auth/portal-login（permitAll，票据即凭据）。
 * ticket 仅在落地页内存活，禁缓存/持久化；60s TTL 由后端裁决（3429）。
 */
export async function portalLogin(ticket: string): Promise<ApiResult<PortalLoginResponse>> {
  return cardholderPost<PortalLoginResponse>(
    '/auth/portal-login',
    { ticket },
    () => ({
      accessToken: 'mock-access-portal-login',
      tokenType: 'Bearer',
      expiresIn: 1800,
      impersonated: true,
      principal: 'cardholder@mock',
      subjectId: 30,
      cardholderId: 30,
    }),
  );
}

type RegisterResult = {
  cardholderId: string;
  cardholderNo?: string;
  principal?: string;
  merchantId?: string;
  status?: string;
  accountId?: string;
};

/** @deprecated EMAIL-REG：裸注册废止，请用 inviteRegister */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- deprecated 存根：参数签名保留以兼容既有调用方，下划线前缀表有意未用（S4 lint 立项，存量豁免）
export async function register(_input: {
  email: string;
  password: string;
  merchantId?: number;
  jurisdiction?: string;
}) {
  return {
    ok: false as const,
    error: '请使用商户邀请链接注册',
  };
}

type InvitePreview = {
  status: string;
  merchantDisplayName?: string;
  expiresAt?: string | null;
};

export async function previewInvite(token: string): Promise<ApiResult<InvitePreview>> {
  return cardholderGet<InvitePreview>(
    `/auth/invites/${encodeURIComponent(token)}`,
    () => ({
      status: 'ACTIVE',
      merchantDisplayName: 'Demo Merchant',
      expiresAt: new Date(Date.now() + 7 * 864e5).toISOString(),
    }),
    { skipAuth: true },
  );
}

export async function inviteRegister(input: {
  inviteToken: string;
  email: string;
  password: string;
  phone?: string;
  jurisdiction?: string;
}): Promise<ApiResult<RegisterResult>> {
  const idem =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `idem-${Date.now()}`;
  return cardholderPost<RegisterResult>(
    '/auth/invite-register',
    {
      inviteToken: input.inviteToken,
      email: input.email,
      password: input.password,
      phone: input.phone,
      jurisdiction: input.jurisdiction || 'HK',
    },
    () => ({
      cardholderId: String(900000 + Math.floor(Math.random() * 999)),
      principal: input.email.toLowerCase(),
      merchantId: String(DEFAULT_MERCHANT_ID),
      status: 'ACTIVE',
    }),
    {
      skipAuth: true,
      headers: { 'Idempotency-Key': idem },
    },
  );
}

type EmailOtpSendResult = {
  emailMasked?: string;
  ttlMinutes?: number;
  cooldownSeconds?: number;
  sentVia?: string;
};

export async function sendEmailCode(): Promise<ApiResult<EmailOtpSendResult>> {
  return cardholderPost<EmailOtpSendResult>(
    '/auth/email/send-code',
    {},
    () => ({
      emailMasked: 'u***@example.com',
      ttlMinutes: 10,
      cooldownSeconds: 60,
      sentVia: 'PLATFORM',
    }),
  );
}

export async function verifyEmailCode(otp: string): Promise<ApiResult<{ emailVerified: boolean }>> {
  return cardholderPost<{ emailVerified: boolean }>(
    '/auth/email/verify',
    { otp },
    () => ({ emailVerified: true }),
  );
}

export { USE_MOCK };
